import axiosInstance from 'axiosInstance';

const apiEndpoint = process.env.REACT_APP_SERVER || '/api/';
const SUMMARY_CACHE_MS = 1500;
const SUMMARY_EVENT_NAME = 'gestodo:messages-summary-updated';

let messagesSummaryPromise = null;
let messagesSummaryCache = null;
let messagesSummaryFetchedAt = 0;

const publishMessagesSummary = summary => {
  if (typeof window === 'undefined') {
    return;
  }

  window.dispatchEvent(
    new window.CustomEvent(SUMMARY_EVENT_NAME, {
      detail: summary || null
    })
  );
};

export const invalidateMessagesSummaryCache = () => {
  messagesSummaryPromise = null;
  messagesSummaryCache = null;
  messagesSummaryFetchedAt = 0;
};

export const getCachedMessagesSummary = () => messagesSummaryCache;

export const subscribeMessagesSummary = listener => {
  if (typeof window === 'undefined' || typeof listener !== 'function') {
    return () => {};
  }

  const handleUpdate = event => {
    listener(event?.detail || null);
  };

  window.addEventListener(SUMMARY_EVENT_NAME, handleUpdate);
  return () => {
    window.removeEventListener(SUMMARY_EVENT_NAME, handleUpdate);
  };
};

export const fetchMessagesOverview = async source => {
  const suffix = source ? `?source=${encodeURIComponent(source)}` : '';
  const response = await axiosInstance.get(`messages/overview${suffix}`);
  return response.data;
};

export const fetchMessagesSummary = async options => {
  const force = Boolean(options?.force);
  const now = Date.now();

  if (
    !force &&
    messagesSummaryCache &&
    now - messagesSummaryFetchedAt < SUMMARY_CACHE_MS
  ) {
    return messagesSummaryCache;
  }

  if (!force && messagesSummaryPromise) {
    return messagesSummaryPromise;
  }

  messagesSummaryPromise = axiosInstance.get('messages/summary')
    .then(response => {
      messagesSummaryCache = response.data;
      messagesSummaryFetchedAt = Date.now();
      publishMessagesSummary(messagesSummaryCache);
      return messagesSummaryCache;
    })
    .finally(() => {
      messagesSummaryPromise = null;
    });

  return messagesSummaryPromise;
};

export const fetchMessageConversationDetail = async conversationId => {
  const response = await axiosInstance.get(`messages/conversations/${conversationId}`);
  return response.data;
};

export const sendInternalMessage = async payload => {
  const response = await axiosInstance.post('messages/internal/send', payload || {});
  invalidateMessagesSummaryCache();
  return response.data;
};

export const searchGlobalContent = async (query, section) => {
  const response = await axiosInstance.get('modules/search/global', {
    params: {
      q: query || '',
      section: section || ''
    }
  });
  return response.data;
};

export const updateInternalGroup = async (conversationId, payload) => {
  const response = await axiosInstance.post(
    `messages/internal/groups/${conversationId}/update`,
    payload || {}
  );
  invalidateMessagesSummaryCache();
  return response.data;
};

export const syncMercadoLibreMessages = async () => {
  const response = await axiosInstance.post('messages/sync', {});
  invalidateMessagesSummaryCache();
  return response.data;
};

export const subscribeMessageStream = handlers => {
  const normalizedApiEndpoint = apiEndpoint.endsWith('/') ? apiEndpoint : `${apiEndpoint}/`;
  const streamUrl = `${window.location.origin}${normalizedApiEndpoint}messages/stream`;
  const eventSource = new window.EventSource(streamUrl, { withCredentials: true });

  if (handlers?.onOpen) {
    eventSource.onopen = handlers.onOpen;
  }

  if (handlers?.onError) {
    eventSource.onerror = handlers.onError;
  }

  if (handlers?.onConversationUpdated) {
    eventSource.addEventListener('message.conversation.updated', event => {
      try {
        handlers.onConversationUpdated(JSON.parse(event.data || '{}'));
      } catch (error) {
        handlers.onConversationUpdated({});
      }
    });
  }

  if (handlers?.onConversationRead) {
    eventSource.addEventListener('message.conversation.read', event => {
      try {
        handlers.onConversationRead(JSON.parse(event.data || '{}'));
      } catch (error) {
        handlers.onConversationRead({});
      }
    });
  }

  return eventSource;
};
